import { Typography, Button } from "@mui/joy";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useState,
} from "react";
import hotkeys from "hotkeys-js";

type Content = {
  title?: string;
  options?: Options | undefined | null;
  actions?: string[];
};

type Options = {
  subtitle?: string | ReactNode;
  render?: (c: any) => ReactElement;
  icon?: ReactNode;
};

export const AskQuestionContext = createContext(
  (title: string, actions: string[], options?: Options | undefined | null) =>
    new Promise<number>((r) => r(1))
);

let resolve: (value: number | PromiseLike<number>) => void;

export default function AskQuestion({ children }: any) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<Content>({});

  hotkeys("enter", function (event, handler) {
    // Prevent the default refresh event under WINDOWS system
    event.preventDefault();
    setOpen(false);
    if (resolve) resolve(1);
  });

  const ask = (
    title: string,
    actions: string[],
    options?: Options | undefined | null
  ) => {
    setContent({
      title,
      actions,
      options,
    });
    setOpen(true);

    return new Promise<number>((r) => {
      resolve = r;
    });
  };

  return (
    <AskQuestionContext.Provider value={ask}>
      {children}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          resolve(0);
        }}
        sx={{ zIndex: 9999999 }}
      >
        {content.title ? (
          <DialogTitle>
            {content.options?.icon}
            {content.title}
          </DialogTitle>
        ) : (
          []
        )}
        <DialogContent>
          <Typography>{content.options?.subtitle}</Typography>
          {content.options?.render ? (
            <Typography>
              {content.options.render(() => {
                setOpen(false);
                resolve(0);
              })}
            </Typography>
          ) : (
            []
          )}
        </DialogContent>
        {content.actions?.length ? (
          <DialogActions>
            {content.actions.map((a, i) => (
              <Button
                variant={
                  Number(i) === (content?.actions?.length ?? 0) - 1
                    ? "solid"
                    : "soft"
                }
                onClick={() => {
                  setOpen(false);
                  resolve(i);
                }}
                color="primary"
                key={a}
              >
                {a}
              </Button>
            ))}
          </DialogActions>
        ) : (
          []
        )}
      </Dialog>
    </AskQuestionContext.Provider>
  );
}

export const useAskQuestion = () => {
  const context = useContext(AskQuestionContext);
  return context;
};
